import { normalizarHabilidadeId } from './skillAliases.js';
import { generateId } from './utils.js';

export const ASSESSMENT_SCHEMA = 2;

export function latestAssessment(assessments = [], studentId) {
  return assessments.filter((item) => item.studentId === studentId || item.alunoId === studentId).sort((a, b) => String(b.data || b.date || '').localeCompare(String(a.data || a.date || '')))[0];
}

/**
 * Produz a visão atual do aluno sem fingir que habilidades antigas foram
 * observadas novamente. Cada habilidade usa apenas seu último registro válido.
 */
export function consolidatedAssessment(assessments = [], studentId) {
  const series = assessments
    .filter((item) => item.studentId === studentId || item.alunoId === studentId)
    .sort((a, b) => String(a.data || a.date || '').localeCompare(String(b.data || b.date || '')));
  if (!series.length) return undefined;

  const pontuacoes = {};
  for (const assessment of series) {
    for (const [rawId, rawValue] of Object.entries(assessment.pontuacoes || assessment.habilidades || {})) {
      if (rawValue === '' || rawValue === null || rawValue === undefined) continue;
      const value = Number(rawValue);
      if (Number.isFinite(value)) pontuacoes[normalizarHabilidadeId(rawId)] = Math.max(0, Math.min(100, value));
    }
  }

  return { ...series.at(-1), studentId, pontuacoes, consolidada: true };
}

export function assessmentScore(assessment, skillId) {
  const canonicalId = normalizarHabilidadeId(skillId);
  if (!assessment?.pontuacoes || !Object.prototype.hasOwnProperty.call(assessment.pontuacoes, canonicalId)) return null;
  const value = Number(assessment.pontuacoes[canonicalId]);
  return Number.isFinite(value) ? value : null;
}

/**
 * Registra somente habilidades efetivamente observadas no dia. Em uma nova data,
 * começa um snapshot parcial vazio em vez de copiar silenciosamente os escores
 * anteriores. Ausência da chave significa "não observado"; zero passa a ser uma
 * pontuação válida ("ainda não demonstrado nas condições observadas").
 */
export function applyAssessmentScore({ assessments = [], studentId, skillId, value, today }) {
  const canonicalId = normalizarHabilidadeId(skillId);
  if (value === '' || value === null || value === undefined) return assessments;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return assessments;
  const score = Math.max(0, Math.min(100, numericValue));
  const current = latestAssessment(assessments, studentId);
  const continuarHoje = Boolean(current?.id) && current.data === today;
  const assessment = {
    ...(continuarHoje ? current : {}),
    id: continuarHoje ? current.id : generateId('ass'),
    studentId,
    data: today,
    tipo: continuarHoje ? current.tipo : (current ? undefined : 'diagnostico_inicial'),
    assessmentSchema: ASSESSMENT_SCHEMA,
    pontuacoes: {
      ...(continuarHoje ? current.pontuacoes : {}),
      [canonicalId]: score,
    },
  };
  const outros = assessments.filter((item) => item.studentId !== studentId);
  const minhas = assessments.filter((item) => item.studentId === studentId);
  const proximasMinhas = continuarHoje ? minhas.map((item) => (item.id === current.id ? assessment : item)) : [...minhas, assessment];
  return [...outros, ...proximasMinhas];
}

/**
 * Desfaz uma observação registrada no dia atual. Se era a única habilidade do
 * snapshot, remove o registro vazio; avaliações anteriores nunca são alteradas.
 */
export function clearAssessmentScore({ assessments = [], studentId, skillId, today }) {
  const canonicalId = normalizarHabilidadeId(skillId);
  const current = latestAssessment(assessments, studentId);
  if (!current?.id || current.data !== today) return assessments;

  const pontuacoes = Object.fromEntries(
    Object.entries(current.pontuacoes || {}).filter(([id]) => normalizarHabilidadeId(id) !== canonicalId),
  );
  if (!Object.keys(pontuacoes).length) return assessments.filter((item) => item.id !== current.id);

  const updated = { ...current, assessmentSchema: ASSESSMENT_SCHEMA, pontuacoes };
  return assessments.map((item) => (item.id === current.id ? updated : item));
}
